import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useInvalidateDeviceList,
  useTriggerVlmServiceAction
} from '@/queries/deviceQueries'
import { showToast } from '@/theme/notifications'
import { Device, VlmAction } from '@/types/device'

type VlmActionConfirmModalProps = {
  opened: boolean
  action: VlmAction
  device: Device
  onClose: () => void
}

type ActionConfig = {
  buttonId: string
  confirmationId: string
}

const ACTION_CONFIG: Record<VlmAction, ActionConfig> = {
  start: {
    buttonId: 'vlm.hardware.start',
    confirmationId: 'vlm.hardware.start.confirmation'
  },
  stop: {
    buttonId: 'vlm.hardware.stop',
    confirmationId: 'vlm.hardware.stop.confirmation'
  },
  restart: {
    buttonId: 'vlm.hardware.restart',
    confirmationId: 'vlm.hardware.restart.confirmation'
  }
}

export const VlmActionConfirmModal = ({
  opened,
  action,
  device,
  onClose
}: VlmActionConfirmModalProps) => {
  const intl = useIntl()
  const config = ACTION_CONFIG[action]

  const { mutateAsync: triggerAction, isPending } = useTriggerVlmServiceAction()
  const { invalidateDeviceList } = useInvalidateDeviceList()

  const handleConfirm = async () => {
    try {
      await triggerAction({ deviceId: device.id, action })
      onClose()
      // Allow time for ThingsBoard to propagate the updated device status
      setTimeout(() => void invalidateDeviceList(), 1000)
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'vlm.hardware.action.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="vlm.hardware.confirm.title" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm">
          <FormattedMessage id={config.confirmationId} />
        </Text>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} onClick={() => void handleConfirm()}>
            <FormattedMessage id={config.buttonId} />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
